import React, { useEffect, useRef, useState } from 'react';

import { PillButton } from 'ds';
import { SERVICES_REQUEST_PATH } from 'routes';

import './Requests.scss';

const ITEMS = [
  {
    initial: 'R',
    avatarClass: 'c',
    name: 'Riya',
    request: 'conference room smells musty',
    meta: 'Cleaning · 9:17 AM',
    status: 'New',
    statusClass: 'new',
    priority: false,
    statusDelay: 0
  },
  {
    initial: 'E',
    avatarClass: 'a',
    name: 'Emmalee',
    request: 'we ran out of Lacroix 😞',
    meta: 'Pantry · 9:11 AM',
    status: 'Ordered',
    statusClass: 'done',
    priority: true,
    statusDelay: 0.6
  },
  {
    initial: 'J',
    avatarClass: 'b',
    name: 'Jordan',
    request: 'need 5 new badges',
    meta: 'Access · 9:06 AM',
    status: 'Requested',
    statusClass: 'done',
    priority: true,
    statusDelay: 1.2
  },
  {
    initial: 'M',
    avatarClass: 'd',
    name: 'Maya',
    request: "there's a leak in the kitchen",
    meta: 'Maintenance · Yesterday',
    status: 'Scheduled',
    statusClass: 'prog',
    priority: false,
    statusDelay: 1.8
  }
];

const FEATURES = [
  {
    label: 'Employees message Codi in Slack',
    sub: 'Codi asks clarifying questions right away to clearly identify the need.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M3 5.5h12M3 9h8m-8 3.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  },
  {
    label: 'Every request in one place',
    sub: 'Prioritize what matters. Codi handles the rest autonomously.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M6 7h6M6 10.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  },
  {
    label: 'Most requests never reach your inbox',
    sub: '94% of employee requests are resolved automatically so you get more time back.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M9 5.5V9l2.5 2.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
];

export default function Requests() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="requests" ref={sectionRef}>
      <div className="requests__container">
        {/* ── Copy ── */}
        <div className={`requests__copy ${visible ? 'requests__copy--visible' : ''}`}>
          <h2 className="requests__heading">
            Nothing falls through
            <br />
            <span className="requests__accent">the cracks.</span>
          </h2>
          <p className="requests__sub">
            Every request is logged, categorized, and handled — so your team always gets what they need without you
            chasing it down.
          </p>
          <PillButton
            type="purpleGradient"
            size="lg"
            text="Get started"
            href={SERVICES_REQUEST_PATH}
            icon="rightArrow"
            iconPosition="right"
            hasShadow
          />
        </div>

        {/* ── Two-column layout ── */}
        <div className="requests__layout">
          {/* Left: 2 feature points + stat chips */}
          <div className={`requests__features ${visible ? 'requests__features--visible' : ''}`}>
            {FEATURES.map((f, i) => (
              <div key={i} className="requests__feat" style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="requests__feat-icon">{f.icon}</div>
                <div>
                  <div className="requests__feat-label">{f.label}</div>
                  <div className="requests__feat-sub">{f.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: inbox panel */}
          <div className={`requests__panel ${visible ? 'requests__panel--visible' : ''}`}>
            <div className="inbox">
              {/* Header */}
              <div className="inbox__header">
                <span className="inbox__title">Employee Requests</span>
                <span className="inbox__badge">1 new</span>
              </div>

              {/* Tabs */}
              <div className="inbox__tabs">
                <span className="inbox__tab inbox__tab--active">All</span>
                <span className="inbox__tab">In progress</span>
                <span className="inbox__tab">Done</span>
              </div>

              {/* Items */}
              <div className="inbox__list">
                {ITEMS.map((item, i) => (
                  <div
                    key={i}
                    className={`inbox__item ${item.priority ? 'inbox__item--priority' : ''} ${visible ? 'inbox__item--visible' : ''}`}
                    style={{ transitionDelay: `${i * 80 + 200}ms` }}
                  >
                    <div className={`inbox__avatar inbox__avatar--${item.avatarClass}`}>{item.initial}</div>
                    <div className="inbox__body">
                      <div className="inbox__who">{item.name}</div>
                      <div className="inbox__request">{item.request}</div>
                      <div className="inbox__meta">{item.meta}</div>
                    </div>
                    <span
                      className={`inbox__status inbox__status--${item.statusClass} ${visible ? 'inbox__status--pop' : ''}`}
                      style={{ transitionDelay: `${item.statusDelay + 0.5}s` }}
                    >
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
